import {useTranslation} from 'react-i18next'
import {DataTable, type Column} from '@/components/data-table'
import {PorQue} from '@/components/ui/por-que'
import type {SolicitudIps} from '@/hooks/salud/medicina_laboral/use-solicitudes-ips'
import {formatDateOnly} from '@/lib/risk'

type FilaMatriz = NonNullable<SolicitudIps['matriz_cargo']>[number]

function columnasMatriz(t: (key: string) => string): Column<FilaMatriz>[] {
  return [
    {key: 'fila', header: t('solicitudIps.matriz.fila'), width: 'w-12', cell: (fila) => fila.numero_fila},
    {
      key: 'peligro',
      header: t('solicitudIps.matriz.peligro'),
      cell: (fila) => `${t(`ipevr.clasificacion.${fila.clasificacion}`)} — ${fila.descripcion}`,
    },
    {
      key: 'valoracion',
      header: t('solicitudIps.matriz.valoracion'),
      width: 'w-40',
      cell: (fila) =>
        [fila.nr !== null ? `NR ${fila.nr}` : null, fila.interpretacion, fila.aceptabilidad]
          .filter(Boolean)
          .join(' · ') || t('solicitudIps.matriz.sinValorar'),
    },
    {
      key: 'controles',
      header: t('solicitudIps.matriz.controles'),
      cell: (fila) =>
        fila.controles.length === 0 ? (
          t('solicitudIps.matriz.sinControles')
        ) : (
          <ul className="list-disc pl-4">
            {fila.controles.map((control) => (
              <li key={`${control.tipo}-${control.texto}`}>{control.texto}</li>
            ))}
          </ul>
        ),
    },
  ]
}

/** Art. 16 lit. c (KAI-154), congelada al generar; sin Matriz en Kairos va el Excel adjunto. */
export function MatrizDelCargo({solicitud}: {solicitud: SolicitudIps}) {
  const {t} = useTranslation()
  if (!solicitud.matriz_cargo || solicitud.matriz_cargo.length === 0) return null
  return (
    <div className="space-y-1" data-testid={`solicitudIps-matriz-${solicitud.id}`}>
      <h4 className="text-xs uppercase tracking-wide text-muted-foreground">{t('solicitudIps.matriz.titulo')}</h4>
      <DataTable
        columns={columnasMatriz(t)}
        rows={solicitud.matriz_cargo}
        rowKey={(fila) => fila.riesgo_fila_id}
        rowTestId={(fila) => `solicitudIps-matriz-fila-${fila.numero_fila}`}
      />
    </div>
  )
}

/** Art. 16 b–e (KAI-154). */
export function DocumentosArt16({solicitud}: {solicitud: SolicitudIps}) {
  const {t} = useTranslation()
  if (solicitud.adjuntos.length === 0) return null
  return (
    <div className="space-y-1" data-testid={`solicitudIps-adjuntos-${solicitud.id}`}>
      <h4 className="text-xs uppercase tracking-wide text-muted-foreground">{t('solicitudIps.adjuntos.titulo')}</h4>
      <ul className="space-y-0.5 text-sm">
        {solicitud.adjuntos.map((adjunto) => (
          <li key={adjunto.id} data-testid={`solicitudIps-adjunto-${solicitud.id}-${adjunto.literal}`}>
            {t(`solicitudIps.adjuntos.literal.${adjunto.literal}`)}
            <span className="font-mono text-xs">
              {' '}
              · {adjunto.filename} · {formatDateOnly(adjunto.fecha)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Art. 16 parágrafo 1 (KAI-154): el correo que salió, con su contenido visible. */
export function CorreosEnviados({solicitud}: {solicitud: SolicitudIps}) {
  const {t} = useTranslation()
  if (solicitud.correos.length === 0) return null
  return (
    <div className="space-y-2" data-testid={`correoIps-enviados-${solicitud.id}`}>
      <h4 className="text-xs uppercase tracking-wide text-muted-foreground">{t('correoIps.enviados.titulo')}</h4>
      {solicitud.correos.map((correo, i) => (
        <div
          key={`${correo.enviado_el}-${i}`}
          className="space-y-1"
          data-testid={`correoIps-enviado-${solicitud.id}-${i}`}>
          <p>
            <span className="font-mono text-xs text-muted-foreground">{formatDateOnly(correo.enviado_el)}</span>{' '}
            {correo.asunto}
          </p>
          <ul className="font-mono text-xs">
            <li>{t('correoIps.enviados.para', {correo: correo.para})}</li>
            {correo.copia.length > 0 && <li>{t('correoIps.enviados.copia', {correos: correo.copia.join(', ')})}</li>}
            {correo.adjuntos.length > 0 && (
              <li>{t('correoIps.enviados.adjuntos', {nombres: correo.adjuntos.join(', ')})}</li>
            )}
            {correo.adjuntos_omitidos.length > 0 && (
              <li>{t('correoIps.enviados.omitidos', {nombres: correo.adjuntos_omitidos.join(', ')})}</li>
            )}
          </ul>
          <PorQue label={t('correoIps.enviados.cuerpo')} testId={`correoIps-cuerpo-${solicitud.id}-${i}`}>
            <pre className="whitespace-pre-wrap font-sans text-xs">{correo.cuerpo_texto}</pre>
          </PorQue>
        </div>
      ))}
    </div>
  )
}
